import { useState } from 'react';
import type { DisplayLogic, Condition, ConditionOp, Question } from './types';
import { titleText } from './types';

type Props = {
  logic: DisplayLogic;
  allQuestions: Question[];
  currentQid: string;
  onChange: (logic: DisplayLogic) => void;
  onClose: () => void;
};

const OP_LABELS: Record<ConditionOp, string> = {
  equals: '等于',
  contains: '包含',
  gt: '大于',
  lt: '小于',
  between: '介于',
};

/** 根据被引用的题型，决定 value 控件 */
function valueKind(q?: Question): 'options' | 'number' | 'text' {
  if (!q) return 'text';
  if (q.type === 'single' || q.type === 'multi') return 'options';
  if (q.type === 'slider') return 'number';
  return 'text';
}

function optionValues(q?: Question): string[] {
  if (!q) return [];
  return (q.options ?? []).map((o) => (typeof o === 'string' ? o : o.value));
}

export default function DisplayLogicModal({ logic, allQuestions, currentQid, onChange, onClose }: Props) {
  const [draft, setDraft] = useState<DisplayLogic>(() => ({
    ...logic,
    conditions: [...(logic.conditions ?? [])],
  }));

  const update = (patch: Partial<DisplayLogic>) => setDraft((d) => ({ ...d, ...patch }));

  const updateCond = (i: number, patch: Partial<Condition>) => {
    const next = [...draft.conditions];
    next[i] = { ...next[i], ...patch };
    update({ conditions: next });
  };

  const addCond = () => {
    const target = allQuestions.find((q) => q.id !== currentQid);
    if (!target) {
      alert('需要有至少 2 道题才能配置显示逻辑');
      return;
    }
    // 默认值：单选题选第一个选项；滑块取 min；其它空字符串
    let defaultValue: unknown = '';
    if (target.type === 'single' || target.type === 'multi') {
      const opts = optionValues(target);
      defaultValue = opts[0] ?? '';
    } else if (target.type === 'slider') {
      defaultValue = (target.params as any)?.min ?? 0;
    }
    update({
      conditions: [
        ...draft.conditions,
        { question: target.id, op: 'equals', value: defaultValue },
      ],
    });
  };

  const removeCond = (i: number) => {
    update({ conditions: draft.conditions.filter((_, idx) => idx !== i) });
  };

  const handleConfirm = () => {
    onChange(draft);
    onClose();
  };

  const candidateQuestions = allQuestions.filter((q) => q.id !== currentQid);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>题目显示逻辑</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">✕</button>
        </div>

        <div className="modal-body">
          {/* 顶部：AND / OR 切换（更显眼的 radio 风格） */}
          <div className="logic-op-row">
            <label className={`logic-op-pill ${draft.operator !== 'OR' ? 'is-on' : ''}`}>
              <input
                type="radio"
                name="logic-op"
                checked={draft.operator !== 'OR'}
                onChange={() => update({ operator: 'AND' })}
              />
              所有条件都满足 <span className="op-tag">AND</span>
            </label>
            <label className={`logic-op-pill ${draft.operator === 'OR' ? 'is-on' : ''}`}>
              <input
                type="radio"
                name="logic-op"
                checked={draft.operator === 'OR'}
                onChange={() => update({ operator: 'OR' })}
              />
              任一条件满足 <span className="op-tag">OR</span>
            </label>
          </div>

          <div className="logic-conds">
            {draft.conditions.map((c, i) => {
              const targetQ = allQuestions.find((q) => q.id === c.question);
              const kind = valueKind(targetQ);
              return (
                <div key={i} className="logic-row">
                  {/* 题目选择 */}
                  <select
                    className="input"
                    value={c.question}
                    onChange={(e) => {
                      const newQ = allQuestions.find((q) => q.id === e.target.value);
                      let newValue: unknown = '';
                      if (newQ) {
                        if (newQ.type === 'single' || newQ.type === 'multi') {
                          newValue = optionValues(newQ)[0] ?? '';
                        } else if (newQ.type === 'slider') {
                          newValue = (newQ.params as any)?.min ?? 0;
                        }
                      }
                      updateCond(i, { question: e.target.value, value: newValue });
                    }}
                  >
                    <option value="">选题目</option>
                    {candidateQuestions.map((q) => (
                      <option key={q.id} value={q.id}>
                        {titleText(q.title) || q.id}
                      </option>
                    ))}
                  </select>

                  {/* 运算选择 */}
                  <select
                    className="input"
                    value={c.op}
                    onChange={(e) => updateCond(i, { op: e.target.value as ConditionOp })}
                  >
                    {Object.entries(OP_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>

                  {/* 值：按题型变控件 */}
                  {kind === 'options' ? (
                    <select
                      className="input logic-value"
                      value={typeof c.value === 'string' ? c.value : ''}
                      onChange={(e) => updateCond(i, { value: e.target.value })}
                    >
                      <option value="">选值</option>
                      {optionValues(targetQ).map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  ) : kind === 'number' ? (
                    c.op === 'between' ? (
                      <span className="input-pair">
                        <input
                          className="input"
                          type="number"
                          placeholder="min"
                          value={Array.isArray(c.value) ? c.value[0] : ''}
                          onChange={(e) => {
                            const min = Number(e.target.value);
                            const max = Array.isArray(c.value) ? Number(c.value[1]) : min;
                            updateCond(i, { value: [min, max] });
                          }}
                        />
                        <span className="dash">~</span>
                        <input
                          className="input"
                          type="number"
                          placeholder="max"
                          value={Array.isArray(c.value) ? c.value[1] : ''}
                          onChange={(e) => {
                            const max = Number(e.target.value);
                            const min = Array.isArray(c.value) ? Number(c.value[0]) : max;
                            updateCond(i, { value: [min, max] });
                          }}
                        />
                      </span>
                    ) : (
                      <input
                        className="input logic-value"
                        type="number"
                        placeholder="请输入"
                        value={
                          typeof c.value === 'number'
                            ? c.value
                            : typeof c.value === 'string'
                            ? c.value
                            : ''
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          updateCond(i, { value: v === '' ? '' : Number(v) });
                        }}
                      />
                    )
                  ) : (
                    <input
                      className="input logic-value"
                      placeholder="请输入"
                      value={typeof c.value === 'string' ? c.value : String(c.value ?? '')}
                      onChange={(e) => updateCond(i, { value: e.target.value })}
                    />
                  )}

                  <button className="icon-btn icon-danger" onClick={() => removeCond(i)} title="删除条件">
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <button className="qcard-link" onClick={addCond}>
            + 添加条件
          </button>

          {candidateQuestions.length === 0 && (
            <div className="hint hint-bad">当前只有这一道题，没法配置显示逻辑。</div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={handleConfirm}>确定</button>
        </div>
      </div>
    </div>
  );
}